import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { withStyles } from "tss-react/mui";
import { DateTime } from "luxon";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ConfirmAbortDialog from "../General/ConfirmAbortDialog";
import RebootPopup from "../General/RebootPopup";
import CustomTextWithLink from "../General/CustomTextWithLink";
import ReplaceLifetime from "../HardwareMonitor/ReplaceLifetime";
import { getLocalizationOrNull } from "../../i18n";
import RswState from "../../enums/robot/rsw/rsw-state";
import { ActionType } from "../../enums/command-hub/command-buttons";
import HttpFetchStatus from "../../enums/api/http-fetch-status";
import SignalRConnectionState from "../../enums/api/signalr-connection-state";
import Role from "../../enums/authentication/role";
import RswMaintenance from "../../enums/robot/rsw/rsw-maintenance";
import { HardwareMonitoringId, ReplacePermissionLevel } from "../../enums/hardware-monitor/hardware-monitoring";
import HttpError from "../../interfaces/api/http-error";
import { CleaningCommandButtonSender, getDefaultCleaningCommandButtonSender } from "../../interfaces/robot/buttons/command-buttons";
import HardwareMonitoring from "../../interfaces/hardware-monitor/hardware-monitoring";
import SystemSerialNumber from "../../interfaces/general/system-serial-data";
import ValidationResult from "../../interfaces/api/validation-result";
import AuthenticationToken, { getAuthTokenRole } from "../../interfaces/authentication/authentication-token";

const ProgressSpinner = withStyles(CircularProgress, (theme) => ({
    root: {
        color: theme.palette.grey[100],
    },
}));

const WarningIcon = withStyles(ReportProblemIcon, (theme) => ({
    root: {
        color: theme.palette.warning.light,
    },
}));

export const mapRswStateToHardwareMonitoring = (state: RswState): HardwareMonitoringId | null => {
    switch (state) {
        case RswState.MaintenanceFilterLifetime:
            return HardwareMonitoringId.Filter;
        default:
            return null;
    }
};

interface HelpRobotDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (cleaningCommandMsg: CleaningCommandButtonSender) => void;
    currentRobotState: RswState;
    isWaitingResponse: boolean;
    hasWarning: boolean;
    activeMaintenances?: RswMaintenance[];
    hardwareMonitoringParams?: {
        hardwareMonitoringData: HardwareMonitoring[],
        onSetHardwareMonitoringParams: (component: HardwareMonitoring, extendable: boolean, dateOfReplacement?: { value: DateTime; validation: ValidationResult }, technician?: string, rating?: number, feedback?: string) => void,
        serialNumberData: SystemSerialNumber | null,
        authTokenData: AuthenticationToken | null
    },
    rebootRobotParams?: {
        rebootData: {
            getStatus: { status: HttpFetchStatus, error: HttpError | null },
            postStatus: { status: HttpFetchStatus, error: HttpError | null },
            isRebootRequired: boolean
        },
        hostHubConnectionStatus: SignalRConnectionState | null,
        updateTimeoutData: { length: number, startTime: number | null },
        onGetIsRebootRequired: () => void,
        onPostStartReboot: () => void,
        onSetBackendDisconnectionExpectation: (disconnection: boolean) => void,
        onSuccessRebootSnackbarOpen: (open: boolean) => void
    }
}

const HelpRobotDialog: React.FC<HelpRobotDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
    currentRobotState,
    isWaitingResponse,
    hasWarning,
    activeMaintenances,
    hardwareMonitoringParams,
    rebootRobotParams
}) => {
    const { t } = useTranslation();

    const [isConfirmRebootDialogOpen, setIsConfirmRebootDialogOpen] = useState(false);
    const [isRebootStepInProgress, setIsRebootStepInProgress] = useState(false);
    const [isConfirmStepsChecked, setIsConfirmStepsChecked] = useState(false);
    const [isConfirmButtonClicked, setIsConfirmButtonClicked] = useState(false);
    const [replacementValues, setReplacementValues] = useState<{ dateOfReplacement: { value: DateTime; validation: ValidationResult }, technician: string, rating: number, feedback: string } | null>(null);

    const hardwareMonitoring = mapRswStateToHardwareMonitoring(currentRobotState as RswState);
    const component = hardwareMonitoringParams?.hardwareMonitoringData.find((hm) => hm.id === hardwareMonitoring);
    const urlKey = getLocalizationOrNull(`robot-interaction-link.${currentRobotState}`);

    const role = (hardwareMonitoringParams?.authTokenData?.token != null ? getAuthTokenRole(hardwareMonitoringParams?.authTokenData?.token) : undefined);
    const extendable = (role === Role.User && component?.replacePermission === ReplacePermissionLevel.All) || role === Role.ServiceAdministrator;

    const rebootRobotStates = [RswState.ErrorNode, RswState.ErrorBaseboard, RswState.ErrorSystem, RswState.ErrorDatabase, RswState.ErrorEmergencyStop, RswState.ErrorEmergencyEvacuation];
    const nonConfirmableStates = [RswState.MaintenanceWeeklyCleaningLimit];
    const isNonConfirmable = nonConfirmableStates.includes(currentRobotState);

    useEffect(() => {
        if (hasWarning || (activeMaintenances && activeMaintenances?.length > 0)) {
            setIsConfirmStepsChecked(false);
            setIsConfirmButtonClicked(false);
        }
    }, [hasWarning, activeMaintenances]);

    useEffect(() => {
        if (component && replacementValues) {
            hardwareMonitoringParams?.onSetHardwareMonitoringParams(component, extendable, replacementValues?.dateOfReplacement, replacementValues?.technician, replacementValues?.rating, replacementValues?.feedback);
        }
    }, [replacementValues]);

    const confirmDialogHandler = () => {
        setIsConfirmButtonClicked(true);
        const cleaningCommandMsg = getDefaultCleaningCommandButtonSender({ action: ActionType.ConfirmMaintenance })
        onConfirm(cleaningCommandMsg);
    }

    const closeDialogHandler = () => {
        onClose();
    }

    const toggleConfirmStepsClickHandler = () => {
        setIsConfirmStepsChecked((prevState) => !prevState);
    };

    const openRebootConfirmationDialogClickHandler = () => {
        closeDialogHandler();
        setIsConfirmRebootDialogOpen(true);
    };

    const confirmRebootClickHandler = () => {
        setIsRebootStepInProgress(true);
        setIsConfirmRebootDialogOpen(false);
        rebootRobotParams?.onSetBackendDisconnectionExpectation(true);
    };

    const cancelRebootClickHandler = () => {
        setIsConfirmRebootDialogOpen(false);
    };

    const rebootStepFinishedHandler = () => {
        setIsRebootStepInProgress(false);
        rebootRobotParams?.onSetBackendDisconnectionExpectation(false);
        rebootRobotParams?.onSuccessRebootSnackbarOpen(true);
    };

    const getSteps = () => {
        const steps: JSX.Element[] = [];
        let stepNumber = 1;

        while (true) {
            const stepText = getLocalizationOrNull(`robot-interaction-step-${stepNumber}.${currentRobotState}`);
            if (!stepText) break;

            steps.push(
                <Typography key={stepNumber}>
                    <Box component="span" mr={2}>{stepNumber}.</Box>
                    <CustomTextWithLink
                        fullText={t(stepText)}
                        link={urlKey ?? t(`robot-interaction-link`)}
                        linkLabelKey={t("robot-interaction-instructions")}
                    />
                </Typography>
            );
            stepNumber++;
        }

        return steps;
    }

    return (
        <React.Fragment>
            <Dialog open={isOpen} onClose={closeDialogHandler} maxWidth="md" fullWidth>
                <DialogTitle>{t("label-help-robot")}</DialogTitle>
                <DialogContent sx={{ pb: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    {hasWarning && (
                        <Alert severity="warning" icon={<WarningIcon />}>{t("robot-interaction-error-resolving-issue")}</Alert>
                    )}
                    <Stack gap={0.5}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>{t("robot-interaction-issue")}</Typography>
                        <Typography>{t(`robot-interaction-description.${currentRobotState}`)}</Typography>
                    </Stack>
                    <Stack gap={0.5}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>{t("robot-interaction-required-steps")}</Typography>
                        {getSteps()}
                    </Stack>
                    {component && extendable && hardwareMonitoringParams?.serialNumberData &&
                        <ReplaceLifetime
                            component={component}
                            replaceHardwareParams={{ serialNumberData: hardwareMonitoringParams.serialNumberData }}
                            hideSaveButton={true}
                            onChange={setReplacementValues}
                        />
                    }
                    {!isNonConfirmable && (
                        <FormControlLabel
                            control={<Checkbox />}
                            checked={isConfirmStepsChecked}
                            onChange={toggleConfirmStepsClickHandler}
                            disabled={isConfirmButtonClicked}
                            sx={{ alignItems: "flex-start", px: 1 }}
                            label={
                                <Stack pt={1} gap={1}>
                                    <Typography>
                                        <Box component="span" sx={{ fontWeight: "bold" }}>{t("robot-interaction-confirm")}</Box>{" "}
                                        {t("robot-interaction-check-confirm")}
                                    </Typography>

                                    <Typography variant="caption">
                                        <CustomTextWithLink
                                            fullText={t(`robot-interaction-check-confirm-note`)}
                                            link={urlKey ?? t(`robot-interaction-link`)}
                                            linkLabelKey={t("robot-interaction-instructions")}
                                        />
                                    </Typography>
                                </Stack>
                            }
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={closeDialogHandler} color="primary">
                        {t("button-close")}
                    </Button>
                    {!isNonConfirmable && (rebootRobotParams && rebootRobotStates.includes(currentRobotState) ? (
                        <Button
                            variant="contained"
                            onClick={openRebootConfirmationDialogClickHandler}
                            disabled={!isConfirmStepsChecked || isWaitingResponse}
                        >
                            {t("button-reboot")}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={confirmDialogHandler}
                            disabled={!isConfirmStepsChecked || isWaitingResponse}
                            startIcon={isWaitingResponse ? <ProgressSpinner size={24} /> : undefined}
                        >
                            {t("button-confirm")}
                        </Button>
                    ))}
                </DialogActions>
            </Dialog>
            {/* rebooting */}
            {isRebootStepInProgress && rebootRobotParams && (
                <RebootPopup
                    isRebootStepInProgress={isRebootStepInProgress}
                    onRebootFinished={rebootStepFinishedHandler}
                    forceReboot
                    rebootData={rebootRobotParams.rebootData}
                    hostHubConnectionStatus={rebootRobotParams.hostHubConnectionStatus}
                    updateTimeoutData={rebootRobotParams.updateTimeoutData}
                    onGetIsRebootRequired={rebootRobotParams.onGetIsRebootRequired}
                    onPostStartReboot={rebootRobotParams.onPostStartReboot}
                />
            )}
            {isConfirmRebootDialogOpen && (
                <ConfirmAbortDialog
                    isOpen={isConfirmRebootDialogOpen}
                    title={t("confirm-reboot-system")}
                    description={t("confirm-reboot-system-description")}
                    abortButtonText={t("button-cancel")}
                    confirmButtonText={t("button-confirm")}
                    onConfirm={confirmRebootClickHandler}
                    onAbort={cancelRebootClickHandler}
                />
            )}
        </React.Fragment>
    )
};

export default HelpRobotDialog;